import { createEffect, type Accessor, type Setter } from "solid-js"
import type { Object3D } from "three"
import { Group, Mesh, MeshBasicMaterial, PlaneGeometry, Raycaster } from "three"
import type { Engine } from "../../../../lib/core/engine/Engine.type"
import { HorizontalOrVertical } from "../../../../lib/core/entity/EntityHeading/HorizontalOrVertical.enum"
import type { TilePositionXY } from "../../../../lib/core/tile_position_xy/TilePositionXY.type"
import { samePosition } from "../../../../lib/core/tile_position_xy/samePosition"
import { sameRowOrColumn } from "../../../../lib/core/tile_position_xy/sameRowOrColumn"
import { HALF_PI } from "../../../../lib/core/util/math/Math"
import firstIfOnlyOne from "../../../../lib/ldsh/firstIfOnlyOne"
import onElementEvent from "../../../../lib/onElementEvent"
import { VERTICES_PER_TILE } from "../../../../lib/three/consts"
import { getEngineThreeMainCamera } from "../../../../lib/three/fn/getSetEngineThreeMainCamera"
import { getEngineThreeMainRenderer } from "../../../../lib/three/fn/getSetEngineThreeMainRenderer"
import useMainScene from "../../../../lib/three/fn/useMainScene"
import usePointerRefVector2 from "../../../../lib/three/fn/usePointerRefVector2"
import { onCleanupRemoveAndDispose } from "../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { useHoveredPositionSignal } from "../../../../rx/signal/useHoveredPositionSignal"
import { useIsMapControlActiveSignal } from "../../../../rx/signal/useIsMapControlActiveSignal"

/**
 * Adds a planeMesh that highlights a full horizontal or vertical line under the hovered tile.
 * Toggling direction is done by clicking the planeMesh.
 */
export default function createEditRowGroup(
  engine: Engine,
  interactiveObjects: Object3D[],
  getSelectedRowPosition: Accessor<TilePositionXY | null>,
  setSelectedRowPosition: Setter<TilePositionXY | null>,
  getOrientation: Accessor<HorizontalOrVertical>,
  setOrientation: Setter<HorizontalOrVertical>,
) {
  const pointerRef = usePointerRefVector2(engine)
  const scene = useMainScene(engine)
  const renderer = getEngineThreeMainRenderer(engine)
  const camera = getEngineThreeMainCamera(engine)
  const [getHoveredPosition] = useHoveredPositionSignal(engine)
  const [getIsMapControlActive] = useIsMapControlActiveSignal(engine)

  const planeY = 8  // Y position for the clickable plane
  const group = new Group()
  onCleanupRemoveAndDispose(group)
  scene.add(group)

  // Create a shared geometry and material for both meshes
  const planeGeometry = new PlaneGeometry(VERTICES_PER_TILE, VERTICES_PER_TILE)
  planeGeometry.rotateX(-HALF_PI)

  const hoverPlaneMaterial = new MeshBasicMaterial({
    color: 0x0000ff,
    transparent: true,
    opacity: 0.2,
  })

  const selectedPlaneMaterial = new MeshBasicMaterial({
    color: 0x0000ff,
    transparent: true,
    opacity: 0.6,
  })

  const hoverRowMesh = new Mesh(planeGeometry, hoverPlaneMaterial)
  const selectedRowMesh = new Mesh(planeGeometry, selectedPlaneMaterial)
  hoverRowMesh.visible = false
  selectedRowMesh.visible = false
  group.add(hoverRowMesh)
  group.add(selectedRowMesh)

  interactiveObjects.push(hoverRowMesh)

  // Update hover plane position and orientation based on hovered tile
  createEffect(() => {
    const hoveredPosition = getHoveredPosition()
    if (getIsMapControlActive() || !hoveredPosition || samePosition(hoveredPosition, getSelectedRowPosition())) {
      hoverRowMesh.visible = false
      return
    }

    const { width, height } = engine.state
    // const halfTile = VERTICES_PER_TILE / 2

    const worldX = hoveredPosition.x * VERTICES_PER_TILE
    const worldZ = hoveredPosition.y * VERTICES_PER_TILE

    const orientation = getOrientation()

    hoverRowMesh.visible = true

    if (orientation === HorizontalOrVertical.Horizontal) {
      // Row across X axis at hovered Z
      hoverRowMesh.position.set((width / 2 - 0.5) * VERTICES_PER_TILE, planeY, worldZ)
      hoverRowMesh.scale.set(width, 1, 1)
    } else {
      // Column across Z axis at hovered X
      hoverRowMesh.position.set(worldX, planeY, (height / 2 - 0.5) * VERTICES_PER_TILE)
      hoverRowMesh.scale.set(1, 1, height)
    }
  })

  // Update selected plane position and orientation
  createEffect(() => {
    const selectedPosition = getSelectedRowPosition()
    if (!selectedPosition) {
      selectedRowMesh.visible = false
      return
    }

    const { width, height } = engine.state
    const worldX = selectedPosition.x * VERTICES_PER_TILE
    const worldZ = selectedPosition.y * VERTICES_PER_TILE
    const orientation = getOrientation()

    selectedRowMesh.visible = true

    if (orientation === HorizontalOrVertical.Horizontal) {
      selectedRowMesh.position.set((width / 2 - 0.5) * VERTICES_PER_TILE, planeY, worldZ)
      selectedRowMesh.scale.set(width, 1, 1)
    } else {
      selectedRowMesh.position.set(worldX, planeY, (height / 2 - 0.5) * VERTICES_PER_TILE)
      selectedRowMesh.scale.set(1, 1, height)
    }
  })

  // Set up raycaster for click event
  const raycaster = new Raycaster()

  onElementEvent(renderer.domElement, 'click', () => {
    raycaster.setFromCamera(pointerRef, camera)
    const intersects = raycaster.intersectObject(hoverRowMesh)
    const intersection = firstIfOnlyOne(intersects)
    if (intersection) {
      const selectedRowPosition = getSelectedRowPosition()
      const orientation = getOrientation()
      const isHorizontal = orientation === HorizontalOrVertical.Horizontal
      const hoveredPosition = getHoveredPosition()

      if (hoveredPosition && sameRowOrColumn(selectedRowPosition, hoveredPosition, orientation)) {
        // Toggle orientation
        setOrientation(
          isHorizontal
            ? HorizontalOrVertical.Vertical
            : HorizontalOrVertical.Horizontal
        )
      }
      setSelectedRowPosition(hoveredPosition)
    }
  })
}